import { memo } from 'react';

type BadgeColor = 'success' | 'warning' | 'error' | 'neutral' | 'primary';

interface StatusMapping {
  label: string;
  color: BadgeColor;
}

interface StatusBadgeProps {
  value: string | null | undefined;
  map?: Record<string, StatusMapping>;
}

const COLOR_STYLES: Record<BadgeColor, { bg: string; text: string; border: string }> = {
  success: {
    bg: 'var(--color-positive-bg)',
    text: 'var(--color-positive)',
    border: 'var(--color-positive-border)'
  },
  warning: {
    bg: 'var(--color-warning-50)',
    text: 'var(--color-warning-600)',
    border: 'var(--color-warning-500)'
  },
  error: {
    bg: 'var(--color-negative-bg)',
    text: 'var(--color-negative)',
    border: 'var(--color-negative-border)'
  },
  neutral: {
    bg: 'var(--color-neutral-100)',
    text: 'var(--color-text-secondary)',
    border: 'var(--color-neutral-300)'
  },
  primary: {
    bg: 'var(--color-primary-50)',
    text: 'var(--color-primary)',
    border: 'var(--color-primary-200)'
  }
};

// Default status mappings for B2B inventory
const DEFAULT_STATUS_MAP: Record<string, StatusMapping> = {
  'active': { label: 'Aktif', color: 'success' },
  'aktif': { label: 'Aktif', color: 'success' },
  'pending': { label: 'Beklemede', color: 'warning' },
  'beklemede': { label: 'Beklemede', color: 'warning' },
  'inactive': { label: 'Pasif', color: 'error' },
  'pasif': { label: 'Pasif', color: 'error' },
  'draft': { label: 'Taslak', color: 'neutral' },
  'taslak': { label: 'Taslak', color: 'neutral' },
  'in_stock': { label: 'Stokta', color: 'success' },
  'stokta': { label: 'Stokta', color: 'success' },
  'low_stock': { label: 'Kritik', color: 'warning' },
  'kritik': { label: 'Kritik', color: 'warning' },
  'out_of_stock': { label: 'Tükendi', color: 'error' },
  'tukendi': { label: 'Tükendi', color: 'error' },
  'approved': { label: 'Onaylandı', color: 'success' },
  'onayli': { label: 'Onaylı', color: 'success' },
  'rejected': { label: 'Reddedildi', color: 'error' },
  'reddedildi': { label: 'Reddedildi', color: 'error' }
};

// Status icons
const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 5l2.5 2.5L8 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
    <path d="M5 1L9 9H1L5 1Z" fillOpacity="0.2" />
    <path d="M5 3.5v2M5 7h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 3l4 4M7 3l-4 4" strokeLinecap="round" />
  </svg>
);

export const StatusBadge = memo(({
  value,
  map
}: StatusBadgeProps) => {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const normalizedValue = value.toLowerCase();
  const statusMap = map || DEFAULT_STATUS_MAP;
  const status = statusMap[normalizedValue] || statusMap[value] || { label: value, color: 'neutral' as BadgeColor };
  const colors = COLOR_STYLES[status.color];

  const icon = status.color === 'success' ? <CheckIcon /> :
               status.color === 'warning' ? <WarningIcon /> :
               status.color === 'error' ? <ErrorIcon /> : null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: '11px',
        fontWeight: 500,
        background: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap'
      }}
    >
      {icon}
      {status.label}
    </span>
  );
});

StatusBadge.displayName = 'StatusBadge';
